import { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Badge } from '@/components/ui/badge';
import { 
  FileText, 
  Calendar,
  Pill,
  Image,
  Stethoscope
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MedicalRecords = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    try {
      const response = await axios.get(`${API}/medical-records`);
      setRecords(response.data);
    } catch (error) {
      console.error('Error fetching records:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRecordIcon = (type) => {
    const icons = {
      lab_result: FileText,
      prescription: Pill,
      imaging: Image,
      diagnosis: Stethoscope,
      default: FileText
    };
    const Icon = icons[type] || icons.default;
    return <Icon className="w-5 h-5" />;
  };

  const getRecordColor = (type) => {
    const colors = {
      lab_result: 'bg-sky-50 text-sky-600',
      prescription: 'bg-purple-50 text-purple-600',
      imaging: 'bg-amber-50 text-amber-600',
      diagnosis: 'bg-emerald-50 text-emerald-600',
      default: 'bg-slate-50 text-slate-600'
    };
    return colors[type] || colors.default;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="medical-records-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Medical Records</h1>
          <p className="text-slate-600 mt-1">Your complete medical history in one place</p>
        </div>

        {/* Records Timeline */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="spinner" />
          </div>
        ) : records.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900">No medical records yet</h3>
            <p className="text-slate-600 mt-2">
              Your lab results and medical records will appear here
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {records.map((record) => (
              <div 
                key={record.id} 
                className="card-clinical hover:shadow-md transition-shadow"
                data-testid={`record-${record.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${getRecordColor(record.record_type)}`}>
                    {getRecordIcon(record.record_type)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className="capitalize">
                        {record.record_type.replace('_', ' ')}
                      </Badge>
                      <span className="text-sm text-slate-500">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        {new Date(record.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <h3 className="font-semibold text-slate-900">{record.title}</h3>
                    <p className="text-slate-600 mt-1">{record.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MedicalRecords;
